/* ===========================================================================
   TSS Quiz, VISION module (v2)
   ---------------------------------------------------------------------------
   Everything to do with the optional photo step lives here:
     - resizing the photo on the phone before it goes anywhere
     - the free, instant quality checks (dark / blurry / tiny)
     - the call to the vision Worker
     - deciding whether to ask the one reconcile question
     - applying findings back onto the answers, within strict limits

   WHY A SEPARATE FILE
   Keeps the change to the live v1 files small, so a rollback is easy and a
   review is readable.

   ===== THE KILL SWITCH =====================================================
   Set VISION_ENDPOINT to '' below and the whole feature disappears. The photo
   step never renders, the quiz behaves exactly as v1, nothing else needs
   changing. Use this the moment anything looks wrong in production.
   ===========================================================================

   THE PHOTO
   Never stored on any server. Sent to the Worker, read, discarded. A small
   320px thumbnail is kept in this browser's localStorage only, so the result
   page can show it back to them, and it is cleared when they retake the quiz.
   Nothing about the image goes to GA4, CustomerLabs or Supabase, only the
   derived text findings.

   NOTE ON FACE DETECTION
   There is deliberately NO face-detection library here. Loading MediaPipe
   would mean a multi-megabyte WebAssembly download for every visitor on a
   mobile connection, to catch a case the Worker already catches for about
   Rs 1. The checks below are the ones that are genuinely free (pure canvas,
   no dependencies, under 100ms). "Is there a face, is it covered, is that
   makeup" is left to the Worker, which is better at it anyway.
   =========================================================================== */

(function () {
  'use strict';

  // ===== CONFIG =============================================================

  // Kill switch. Empty string = feature off, quiz runs exactly as v1.
  var VISION_ENDPOINT = 'https://tss-quiz-vision.tsschatbot.workers.dev/analyse';

  // How long to wait for the Worker before giving up and just showing the
  // routine. Opus 5 runs 8 to 15s, Sonnet 5 runs 4 to 6s. If you switch the
  // Worker's VISION_MODEL to Sonnet you can lower this to about 15000.
  var ANALYSE_TIMEOUT_MS = 35000;   // a real Sonnet 5 read measured 15.9s,
                                    // and 22s left too little headroom
  var EXTRA_PHOTO_TIMEOUT_MS = 8000; // added per side photo, since each one
                                     // is more for the model to read

  // How long to wait for the one-off /health check before deciding the photo
  // step cannot be offered. Short on purpose: nobody should wait on it.
  var PREFLIGHT_TIMEOUT_MS = 3500;

  // The photo is resized to this on the long edge before sending. Bigger is
  // not better: it costs more per scan and uploads slower on mobile data.
  var SEND_MAX_EDGE = 1024;
  var THUMB_MAX_EDGE = 320;
  var JPEG_QUALITY = 0.8;

  // Client-side quality gates. Deliberately LOOSE. These exist to catch the
  // obviously unusable photos for free. Anything borderline is better judged
  // by the Worker, which can give a more specific reason. Being too strict
  // here means rejecting photos that would have worked, which is the worse
  // failure. Retune only with real numbers from the calibration bench.
  var MIN_SOURCE_EDGE = 480;   // original photo smaller than this = unusable
  var MIN_MEAN_LUMA   = 40;    // 0-255. Below this is genuinely dark.
  var MAX_MEAN_LUMA   = 225;   // Above this is blown out.
  var MAX_CLIP_RATIO  = 0.35;  // more than 35% of pixels pinned at 0 or 255
  var MIN_BLUR_VAR    = 40;    // Laplacian variance. Sharp phone photos run
                               // 200 to 2000. Under 40 is properly soft.

  var THUMB_KEY_PREFIX = 'tss_quiz_photo_v1_';

  // TWO confidence bars, because there are two very different situations.
  //
  // ACT_ON_CONFIDENCE: the photo changing something the customer never sees
  // or approves (the questions-first path, the result-page re-offer). Silent,
  // so it has to be sure.
  var ACT_ON_CONFIDENCE = 'high';
  //
  // CONFIRM_MIN_CONFIDENCE: the photo-first path, where every read is shown to
  // the customer and they confirm it before it counts. Their confirmation is
  // the safety check, so the bar is lower.
  //
  // This used to be 'high' as well, and that was a bug: the vision prompt
  // tells the model to be stingy with 'high', and in a real test on a photo
  // of obvious, severe acne it returned medium on every single field. With a
  // 'high' bar the confirm screen came up empty for essentially every photo
  // and showed the "photo did not tell us much" message. Measured, not guessed.
  var CONFIRM_MIN_CONFIDENCE = 'medium';

  // ===== WHAT THE PHOTO IS ALLOWED TO DO ===================================
  // Flip any of these to false to switch that behaviour off on its own,
  // without touching the code below. Start conservative, open them up as the
  // calibration numbers justify it.
  var AUTHORITY = {
    add_marks:        true,   // photo shows marks they did not mention
    add_texture:      true,   // photo shows congestion they did not mention
    add_irritation:   true,   // photo shows redness, routes to barrier_first
    refine_skin_type: true,   // only ever when they answered "not sure"
    reconcile_acne:   true    // ask the one question about active acne
  };

  // Concern VALUES the engine understands. These are the strings CONCERN_MAP
  // in tss-quiz-personas.js translates into buckets. Do not invent new ones,
  // an unknown value is silently dropped and the finding does nothing.
  var CONCERN_VALUE = {
    marks:       'post_acne_marks',
    texture:     'uneven_texture',
    irritation:  'irritated_sensitive',
    active_acne: 'small_white_red_pimples',  // the mildest acne value
    maintenance: 'maintenance'
  };

  // Every value that maps to the active_acne bucket. Needed for a removal.
  var ACNE_VALUES = [
    'red_painful_pimples', 'pus_filled_acne',
    'large_cystic_pimples', 'small_white_red_pimples'
  ];

  // ===== SMALL HELPERS ======================================================

  function log() {
    if (!window.console || !console.log) return;
    var args = ['[TSS Quiz Vision]'].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  function isEnabled() {
    return !!VISION_ENDPOINT;
  }

  // ===== PREFLIGHT ==========================================================
  // Checks ONCE per page that the endpoint is really the vision Worker before
  // we offer anyone a photo.
  //
  // This exists because of a real incident: the vision Worker got overwritten
  // with the copy Worker's code, so every /analyse returned
  // {"error":"missing session_id / answers / persona"}. From the customer's
  // side that was indistinguishable from a bad photo, and from the operator's
  // side it was invisible. Now a wrong or dead endpoint means the photo is
  // never offered at all, and the console says exactly what to fix.
  var preflightPromise = null;
  var healthInfo = null;   // the Worker's /health reply, once preflight passes

  function preflight() {
    if (preflightPromise) return preflightPromise;
    if (!isEnabled()) {
      preflightPromise = Promise.resolve(false);
      return preflightPromise;
    }

    var healthUrl = VISION_ENDPOINT.replace(/\/analyse\/?$/, '') + '/health';

    preflightPromise = new Promise(function (resolve) {
      var done = false;
      function finish(ok, why) {
        if (done) return;
        done = true;
        if (!ok) {
          log('PREFLIGHT FAILED, photo step will not be offered. ' + why);
          log('Check that ' + healthUrl + ' returns {"ok":true,"model":"..."}. ' +
              'If it returns anything else, the Worker at that URL is not ' +
              'tss-quiz-vision. Re-paste backend/tss-quiz-vision-worker.js into it.');
        }
        resolve(ok);
      }

      setTimeout(function () { finish(false, 'health check timed out'); }, PREFLIGHT_TIMEOUT_MS);

      fetch(healthUrl)
        .then(function (r) { return r.json()['catch'](function () { return null; }); })
        .then(function (d) {
          if (!d || d.ok !== true || !d.model) {
            finish(false, 'health check returned an unexpected shape: ' + JSON.stringify(d));
            return;
          }
          // The Worker tells us whether THIS browser would be allowed to post
          // a photo. Without this check a token/origin misconfiguration passes
          // preflight and then rejects every single upload with a 401, which
          // reads to the customer as "couldn't read that one properly".
          if (d.authorised === false) {
            finish(false, 'this origin (' + (d.origin_seen || window.location.origin) +
              ') is not authorised by the Worker. Add it to ALLOWED_ORIGINS, or ' +
              'update DEFAULT_ORIGINS in tss-quiz-vision-worker.js.');
            return;
          }
          healthInfo = d;
          finish(true, '');
        })
        ['catch'](function (e) {
          finish(false, 'health check could not be reached: ' + ((e && e.message) || e));
        });
    });

    return preflightPromise;
  }

  // How many photos the Worker accepts. An older Worker has no max_images
  // field, which means front photo only: the side tiles are simply not shown.
  function maxImages() {
    var n = healthInfo && healthInfo.max_images;
    if (typeof n !== 'number' || n < 2) return 1;
    return Math.min(n, 3);
  }

  // ===== IMAGE LOADING + RESIZE ============================================

  // Decodes the file into something we can draw, applying the EXIF rotation
  // that phones (iPhones especially) bake into portrait photos. Without this
  // a perfectly good selfie can arrive sideways and read as unusable.
  function decodeImage(file) {
    if (window.createImageBitmap) {
      try {
        return createImageBitmap(file, { imageOrientation: 'from-image' })
          .catch(function () { return decodeViaImgTag(file); });
      } catch (e) {
        return decodeViaImgTag(file);
      }
    }
    return decodeViaImgTag(file);
  }

  function decodeViaImgTag(file) {
    return new Promise(function (resolve, reject) {
      var fr = new FileReader();
      fr.onerror = function () { reject(new Error('could not read the file')); };
      fr.onload = function () {
        var img = new Image();
        img.onerror = function () { reject(new Error('not a readable image')); };
        img.onload = function () { resolve(img); };
        img.src = fr.result;
      };
      fr.readAsDataURL(file);
    });
  }

  function drawScaled(src, maxEdge) {
    var sw = src.width, sh = src.height;
    var scale = Math.min(1, maxEdge / Math.max(sw, sh));
    var w = Math.max(1, Math.round(sw * scale));
    var h = Math.max(1, Math.round(sh * scale));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d');
    ctx.drawImage(src, 0, 0, w, h);
    return c;
  }

  // ===== QUALITY CHECKS ====================================================

  function exposureStats(canvas) {
    // Sample at a small size, this only needs to be approximately right.
    var s = drawScaled(canvas, 160);
    var d = s.getContext('2d').getImageData(0, 0, s.width, s.height).data;
    var sum = 0, clipped = 0, n = s.width * s.height;
    for (var i = 0; i < d.length; i += 4) {
      var y = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      sum += y;
      if (y <= 4 || y >= 251) clipped++;
    }
    return { mean: sum / n, clipRatio: clipped / n };
  }

  function blurVariance(canvas) {
    // Laplacian variance: run a 3x3 edge kernel over the greyscale image and
    // measure how much the response varies. A sharp photo has lots of strong
    // edges (high variance). A blurry one is flat (low variance).
    var s = drawScaled(canvas, 320);
    var w = s.width, h = s.height;
    var d = s.getContext('2d').getImageData(0, 0, w, h).data;

    var grey = new Float32Array(w * h);
    for (var i = 0, p = 0; i < d.length; i += 4, p++) {
      grey[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    }

    var sum = 0, sumSq = 0, count = 0;
    for (var y = 1; y < h - 1; y++) {
      for (var x = 1; x < w - 1; x++) {
        var o = y * w + x;
        var lap = grey[o - w] + grey[o + w] + grey[o - 1] + grey[o + 1] - 4 * grey[o];
        sum += lap; sumSq += lap * lap; count++;
      }
    }
    if (!count) return 0;
    var mean = sum / count;
    return (sumSq / count) - (mean * mean);
  }

  // Returns a reason code matching the Worker's vocabulary, or null if fine.
  function localQualityReason(sourceW, sourceH, canvas) {
    if (Math.min(sourceW, sourceH) < MIN_SOURCE_EDGE) return 'too_small';

    var ex = exposureStats(canvas);
    if (ex.mean < MIN_MEAN_LUMA) return 'too_dark';
    if (ex.mean > MAX_MEAN_LUMA) return 'too_bright';
    if (ex.clipRatio > MAX_CLIP_RATIO) {
      return ex.mean < 128 ? 'too_dark' : 'too_bright';
    }

    if (blurVariance(canvas) < MIN_BLUR_VAR) return 'too_blurry';

    return null;
  }

  // ===== PUBLIC: process a chosen file =====================================

  // Resolves { ok, reason, b64, mime, thumb, width, height }.
  // ok:false with a reason code means show the matching retake message.
  function processFile(file) {
    if (!file || !/^image\//.test(file.type || '')) {
      return Promise.resolve({ ok: false, reason: 'not_a_face' });
    }
    return decodeImage(file).then(function (src) {
      var sourceW = src.width, sourceH = src.height;
      var big = drawScaled(src, SEND_MAX_EDGE);

      var reason = localQualityReason(sourceW, sourceH, big);
      if (reason) {
        if (src.close) { try { src.close(); } catch (e) {} }
        return { ok: false, reason: reason };
      }

      var sendUrl = big.toDataURL('image/jpeg', JPEG_QUALITY);
      var thumbUrl = drawScaled(big, THUMB_MAX_EDGE).toDataURL('image/jpeg', 0.7);
      if (src.close) { try { src.close(); } catch (e) {} }

      return {
        ok: true,
        reason: null,
        b64: sendUrl.split(',')[1],
        mime: 'image/jpeg',
        thumb: thumbUrl,
        width: big.width,
        height: big.height
      };
    }).catch(function (err) {
      log('processFile failed', err);
      return { ok: false, reason: 'unreadable' };
    });
  }

  // ===== PUBLIC: call the Worker ===========================================

  function analyse(opts) {
    opts = opts || {};
    if (!isEnabled()) {
      return Promise.resolve({ ok: false, error: 'vision disabled' });
    }

    var ctrl = window.AbortController ? new AbortController() : null;
    var started = (window.performance && performance.now) ? performance.now() : 0;
    var settled = false;
    var timer = null;

    function elapsed() {
      return started ? Math.round(performance.now() - started) : null;
    }

    // opts.images: [{ view: 'front'|'left'|'right', b64, mime }]. The older
    // single-photo form (opts.b64) still works. If the Worker cannot take more
    // than one photo, only the front is sent, in the body shape it expects.
    var images = opts.images || [{ view: 'front', b64: opts.b64, mime: opts.mime }];
    var body;
    if (maxImages() > 1) {
      body = {
        images: images.slice(0, maxImages()).map(function (im) {
          return { view: im.view, image_base64: im.b64, media_type: im.mime || 'image/jpeg' };
        }),
        answers: opts.answers || null
      };
    } else {
      var front = images.filter(function (im) { return im.view === 'front'; })[0] || images[0];
      body = {
        image_base64: front.b64,
        media_type: front.mime || 'image/jpeg',
        answers: opts.answers || null
      };
    }
    var sentCount = body.images ? body.images.length : 1;
    var limitMs = ANALYSE_TIMEOUT_MS + EXTRA_PHOTO_TIMEOUT_MS * (sentCount - 1);

    var init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    };
    if (ctrl) init.signal = ctrl.signal;

    // The timeout is a RACE, not just an abort. AbortController is missing on
    // some older Android WebViews, and relying on it alone meant the caller
    // could wait forever on a request that never came back, leaving the
    // customer on a spinner with no route to their routine.
    var timeout = new Promise(function (resolve) {
      timer = setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) {} }
        resolve({ ok: false, error: 'timeout', latency_ms: elapsed() });
      }, limitMs);
    });

    var request = fetch(VISION_ENDPOINT, init)
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || d.ok !== true || !d.analysis) {
          // A response mentioning session_id/persona means this URL is running
          // the COPY Worker, not the vision Worker. Name it so it is obvious.
          var msg = (d && d.error) || 'bad response';
          if (/session_id|persona/.test(String(msg))) {
            msg = 'wrong_worker: ' + VISION_ENDPOINT + ' is running the copy Worker, not tss-quiz-vision';
            log(msg);
          }
          return { ok: false, error: msg, latency_ms: elapsed() };
        }
        // An older Worker returns no views. Then every photo it was sent
        // counts as used, which for that Worker is only ever the front.
        if (!d.analysis.views || !d.analysis.views.length) {
          d.analysis.views = (body.images || [{ view: 'front' }]).map(function (im) {
            return { view: im.view, used: true, issue: 'none' };
          });
        }
        return { ok: true, analysis: d.analysis, model: d.model, latency_ms: elapsed(), sent: sentCount };
      })
      ['catch'](function (err) {
        var aborted = err && err.name === 'AbortError';
        log('analyse failed', err);
        return {
          ok: false,
          error: aborted ? 'timeout' : String((err && err.message) || err),
          latency_ms: elapsed()
        };
      });

    return Promise.race([request, timeout]).then(function (res) {
      if (settled) return res;
      settled = true;
      if (timer) clearTimeout(timer);
      return res;
    });
  }

  // ===== READING THE ANALYSIS ==============================================

  function readOf(analysis, key) {
    var r = analysis && analysis.reads;
    return (r && r[key]) ? r[key] : null;
  }

  function confidentAtLeast(read, minBand) {
    if (!read || read.confidence !== ACT_ON_CONFIDENCE) return false;
    var order = ['none', 'mild', 'moderate', 'marked'];
    var have = order.indexOf(read.value);
    var need = order.indexOf(minBand);
    return have !== -1 && need !== -1 && have >= need;
  }

  function confidentNone(read) {
    return !!read && read.confidence === ACT_ON_CONFIDENCE && read.value === 'none';
  }

  // Checks the concern VALUES the engine actually reads, not just the UI's
  // concern_buckets list, so this stays right even if the two ever drift.
  var CONF_ORDER = ['low', 'medium', 'high'];
  var DENSITY_KNOWN = ['none', 'mild', 'moderate', 'marked'];
  var SKIN_KNOWN = ['oily', 'dry', 'combination', 'normal'];

  function meets(read, min) {
    return !!read && CONF_ORDER.indexOf(read.confidence) >= CONF_ORDER.indexOf(min);
  }

  // THE ONE RULE for the photo-first path. The confirm screen shows exactly
  // these reads, and seed mode applies exactly these reads, so what the
  // customer approved and what we build can never disagree.
  //
  // Returns { skin_type, active_acne, marks, texture, redness }, each either a
  // read object or null. A null field is not shown and not applied.
  function confirmableReads(analysis) {
    var out = { skin_type: null, active_acne: null, marks: null, texture: null, redness: null };
    if (!analysis || analysis.usable !== true) return out;
    var r = analysis.reads || {};

    if (AUTHORITY.refine_skin_type && r.skin_type &&
        meets(r.skin_type, CONFIRM_MIN_CONFIDENCE) &&
        SKIN_KNOWN.indexOf(r.skin_type.value) !== -1) {
      out.skin_type = r.skin_type;
    }

    // 'none' is included on purpose: "Breakouts: nothing obvious" is useful to
    // show, and it is what makes the read feel like someone actually looked.
    if (r.active_acne && meets(r.active_acne, CONFIRM_MIN_CONFIDENCE) &&
        DENSITY_KNOWN.indexOf(r.active_acne.value) !== -1) {
      out.active_acne = r.active_acne;
    }
    if (AUTHORITY.add_marks && r.marks && meets(r.marks, CONFIRM_MIN_CONFIDENCE) &&
        DENSITY_KNOWN.indexOf(r.marks.value) !== -1) {
      out.marks = r.marks;
    }
    if (AUTHORITY.add_texture && r.texture && meets(r.texture, CONFIRM_MIN_CONFIDENCE) &&
        DENSITY_KNOWN.indexOf(r.texture.value) !== -1) {
      out.texture = r.texture;
    }
    // Redness only surfaces at moderate and above. Mild redness is simply what
    // inflamed spots look like, and reading it as sensitive skin would put
    // nearly everyone with acne onto the go-slow barrier protocol.
    if (AUTHORITY.add_irritation && r.redness && meets(r.redness, CONFIRM_MIN_CONFIDENCE) &&
        (r.redness.value === 'moderate' || r.redness.value === 'marked')) {
      out.redness = r.redness;
    }
    return out;
  }

  // For the path where the customer rejected the read and answered for
  // themselves. Their answers win, and the result page has to say so wherever
  // the photo saw it differently. Returns a list of disagreements.
  function findConflicts(analysis, answers) {
    var c = confirmableReads(analysis);
    var out = [];
    if (!answers) return out;

    function presence(field, bucket) {
      if (!c[field]) return;
      var photoSaw = c[field].value !== 'none';
      var theySaid = hasBucket(answers, bucket);
      if (photoSaw && !theySaid) out.push({ field: field, photo: 'present', theirs: 'absent' });
      if (!photoSaw && theySaid) out.push({ field: field, photo: 'absent', theirs: 'present' });
    }
    presence('active_acne', 'active_acne');
    presence('marks', 'marks');
    presence('texture', 'texture');

    // Only a disagreement if they also said their skin rarely reacts (or
    // skipped Q6). "Sometimes" or "most things" already covers it.
    if (c.redness && !hasBucket(answers, 'irritation') &&
        answers.sensitivity !== 'a_bit' && answers.sensitivity !== 'very') {
      out.push({ field: 'redness', photo: 'present', theirs: 'absent' });
    }

    if (c.skin_type && answers.skin_type && answers.skin_type !== 'not_sure' &&
        answers.skin_type !== c.skin_type.value) {
      out.push({ field: 'skin_type', photo: c.skin_type.value, theirs: answers.skin_type });
    }
    return out;
  }

  // THE photo-first path's apply step. The confirm card shows every concern
  // row, pre-set where the photo was confident and blank where it was not, and
  // the customer checks or changes each one. Their final Yes/No per row is
  // what gets written, never the raw read. A row the photo could not see is
  // one they answered themselves, so "couldn't tell" can no longer turn into
  // "you don't have it".
  //
  // choices: { skin_type?: 'oily'|..., active_acne: 'yes'|'no', marks, texture,
  //            redness?: 'yes'|'no' }
  // Rebuilds concerns from scratch, so it is safe to call again on resume.
  function applyChoices(choices, answers) {
    var out = { added: [], removed: [], skin_type_set: null, removal_outcome: null };
    if (!choices || !answers) return out;

    answers.concerns = [];
    answers.concern_buckets = [];

    function add(bucket) {
      var value = CONCERN_VALUE[bucket];
      if (!value) return;
      answers.concerns.push(value);
      answers.concern_buckets.push(bucket);
      out.added.push(bucket);
    }

    if (choices.active_acne === 'yes') add('active_acne');
    if (choices.marks === 'yes') add('marks');
    if (choices.texture === 'yes') add('texture');
    if (choices.redness === 'yes') add('irritation');
    if (!answers.concerns.length) add('maintenance');

    // 'not_sure' is a real Q1 answer; the engine treats it as combination.
    if (choices.skin_type &&
        (SKIN_KNOWN.indexOf(choices.skin_type) !== -1 || choices.skin_type === 'not_sure')) {
      answers.skin_type = choices.skin_type;
      out.skin_type_set = choices.skin_type;
    }
    return out;
  }

  function hasBucket(answers, key) {
    var concerns = (answers && answers.concerns) || [];
    var buckets = (answers && answers.concern_buckets) || [];
    if (buckets.indexOf(key) !== -1) return true;
    if (key === 'active_acne') {
      for (var i = 0; i < concerns.length; i++) {
        if (ACNE_VALUES.indexOf(concerns[i]) !== -1) return true;
      }
      return false;
    }
    return concerns.indexOf(CONCERN_VALUE[key]) !== -1;
  }

  // ===== PUBLIC: should we ask the one reconcile question? =================

  // Returns null, or { variant, options: [{value,label}] }. The question text
  // itself lives in tss-quiz-copy.js, this only decides WHICH one to ask.
  //
  // Only ever about active acne. It is the single field where being wrong
  // changes the routine enough to be worth interrupting for, and asking about
  // several things in a row would undo the whole "one question" design.
  function buildReconcile(analysis, answers) {
    if (!AUTHORITY.reconcile_acne) return null;
    if (!analysis || analysis.usable !== true) return null;

    var acne = readOf(analysis, 'active_acne');
    if (!acne || acne.confidence !== ACT_ON_CONFIDENCE) return null;

    var claims = hasBucket(answers, 'active_acne');

    // They said they break out, the photo shows nothing active.
    if (claims && acne.value === 'none') return { variant: 'clear' };

    // They did not mention breakouts, the photo shows a clear amount.
    if (!claims && (acne.value === 'moderate' || acne.value === 'marked')) {
      return { variant: 'visible' };
    }

    return null;
  }

  // ===== PUBLIC: apply findings to the answers =============================

  /*  THE SAFETY RULE THAT MATTERS
      ----------------------------
      This function only ever edits `answers.concerns` (and keeps
      `answers.concern_buckets` in sync for the UI and analytics). It writes
      the SAME concern value strings the quiz itself writes, so the existing
      rules engine treats a vision-added concern exactly as if the user had
      ticked that box. No routine logic is duplicated or bypassed.

      WHY answers.concerns AND NOT answers.concern_buckets:
      compute() in tss-quiz-personas.js builds its bucket set with
      bucketsFrom(answers.concerns). `concern_buckets` is the quiz UI's own
      record of what was ticked on Q2 and does NOT feed the routine. Editing
      only concern_buckets would change the chips on the result page and
      nothing else. Both are updated here so they never disagree.

      ADDING a concern is always safe. The engine's cluster priority runs
      marks+acne > texture+acne > acne > marks > texture > maintenance, so an
      addition can only move someone to a RICHER routine. It can never drop a
      product they needed.

      REMOVING active acne is the one genuinely dangerous edit: marks+acne
      (which includes the acne treatment) would collapse to marks-only (which
      does not). So it happens in exactly one place, when the user themselves
      answers the reconcile question to say they are past their breakouts.
      Never from the photo alone, at any confidence.
  */
  //
  //  SEED MODE (opts.seed === true)
  //  On the photo-FIRST path the photo is the only source of concerns, so it
  //  must also be allowed to add active acne. That is safe there and only
  //  there, because the confirm screen shows the user every read and lets them
  //  reject the lot. On the questions-first path the user has already told us,
  //  so adding acne still needs their answer to the reconcile question.
  function applyFindings(analysis, answers, reconcileValue, opts) {
    var seed = !!(opts && opts.seed);
    var out = { added: [], removed: [], skin_type_set: null, removal_outcome: null };
    if (!analysis || analysis.usable !== true || !answers) return out;

    answers.concerns = answers.concerns || [];
    answers.concern_buckets = answers.concern_buckets || [];

    // Adds a concern VALUE (what the engine reads) plus its BUCKET (what the
    // UI and analytics read). `bucket` is recorded as the human-facing label.
    function add(bucket) {
      var value = CONCERN_VALUE[bucket];
      if (!value) return;
      var changed = false;
      if (answers.concerns.indexOf(value) === -1) {
        answers.concerns.push(value);
        changed = true;
      }
      if (answers.concern_buckets.indexOf(bucket) === -1) {
        answers.concern_buckets.push(bucket);
        changed = true;
      }
      if (changed && out.added.indexOf(bucket) === -1) out.added.push(bucket);
    }

    function removeAcne() {
      var before = answers.concerns.length;
      answers.concerns = answers.concerns.filter(function (c) {
        return ACNE_VALUES.indexOf(c) === -1;
      });
      var bi = answers.concern_buckets.indexOf('active_acne');
      if (bi !== -1) answers.concern_buckets.splice(bi, 1);
      if (answers.concerns.length !== before || bi !== -1) out.removed.push('active_acne');
    }

    // ---- 1. The user's own reconcile answer, the only path to a removal ----
    if (reconcileValue === 'past_breakouts') {
      removeAcne();

      // Their breakout frequency described acne we are no longer treating, so
      // leaving it set would put a stale "Breakout frequency" chip on the
      // result page. This is not the photo overruling a self-report: the user
      // just told us the acne has settled. Clearing it changes no routine,
      // because the marks and maintenance routines do not read frequency.
      answers.acne_freq = null;

      // Someone just past their breakouts almost always has marks left. Give
      // them something to actually treat rather than an empty routine.
      if (AUTHORITY.add_marks && confidentAtLeast(readOf(analysis, 'marks'), 'mild')) {
        add('marks');
      }
      if (!answers.concerns.length) add('maintenance');
      // Lets the result page tell "now focused on the marks" apart from
      // "nothing active left to treat", which read the same before.
      out.removal_outcome = (answers.concerns.indexOf(CONCERN_VALUE.marks) !== -1)
        ? 'marks' : 'maintenance';
    } else if (reconcileValue === 'treat_it') {
      add('active_acne');
    }
    // 'still_breaks_out' and 'unusual_for_me' both mean "leave it alone".

    // ---- 2a. SEED MODE: the photo-first path ------------------------------
    // The customer has seen and confirmed every one of these reads, so a
    // confirmed read counts exactly like a ticked box on Q2. Applies the SAME
    // set the confirm screen showed, via confirmableReads(), and then returns,
    // so the silent-path rules below never run on this path.
    if (seed) {
      var c = confirmableReads(analysis);
      if (c.active_acne && c.active_acne.value !== 'none') add('active_acne');
      if (c.marks && c.marks.value !== 'none') add('marks');
      if (c.texture && c.texture.value !== 'none') add('texture');
      if (c.redness) add('irritation');
      if (c.skin_type && !answers.skin_type) {
        answers.skin_type = c.skin_type.value;
        out.skin_type_set = c.skin_type.value;
      }
      // Nothing to treat is still a routine, not the engine's bare fallback.
      if (!answers.concerns.length) add('maintenance');
      return out;
    }

    // ---- 2b. SILENT PATH: nobody confirms these, so high confidence only ---

    // Marks. People stop noticing their own marks, and this is the concern
    // Indian customers care most about. Allowed from mild upwards.
    if (AUTHORITY.add_marks && confidentAtLeast(readOf(analysis, 'marks'), 'mild')) {
      add('marks');
    }

    // Texture. Held to moderate and above on purpose: adding texture pulls in
    // a second cleanser, and doing that off a faint read would land as an
    // upsell rather than a finding.
    if (AUTHORITY.add_texture && confidentAtLeast(readOf(analysis, 'texture'), 'moderate')) {
      add('texture');
    }

    // Visible irritation. Goes in as a concern so the engine stamps
    // barrier_first and the result page shows the go-slow note.
    // Careful: on someone with NO treatable concern this also triggers the
    // engine's no-actives irritation override. That is the correct outcome.
    if (AUTHORITY.add_irritation && confidentAtLeast(readOf(analysis, 'redness'), 'moderate')) {
      add('irritation');
    }

    // ---- 3. Skin type, only when they told us they did not know -----------
    // compute() silently turns 'not_sure' into 'combination', so a confident
    // read here is strictly better than the default.
    if (AUTHORITY.refine_skin_type && answers.skin_type === 'not_sure') {
      var st = readOf(analysis, 'skin_type');
      if (st && st.confidence === ACT_ON_CONFIDENCE &&
          ['oily', 'dry', 'combination', 'normal'].indexOf(st.value) !== -1) {
        answers.skin_type = st.value;
        out.skin_type_set = st.value;
      }
    }

    // ---- 4. Never touched, whatever the photo says ------------------------
    //   acne_freq       a photo is one day, it cannot see frequency
    //   fungal_acne     not diagnosable from a selfie
    //   rosacea         same
    //   sensitivity     self-reported only
    // Their absence here is the point. Do not add them later without
    // re-running the calibration bench.

    return out;
  }

  // ===== THUMBNAIL STORAGE (this browser only) =============================

  function saveThumb(sessionId, dataUrl) {
    if (!sessionId || !dataUrl) return;
    try {
      localStorage.setItem(THUMB_KEY_PREFIX + sessionId, dataUrl);
    } catch (e) {
      // Quota, or private browsing. Not worth failing the quiz over, the
      // result page simply shows the findings without the photo.
      log('could not store thumbnail', e && e.message);
    }
  }

  function loadThumb(sessionId) {
    if (!sessionId) return null;
    try { return localStorage.getItem(THUMB_KEY_PREFIX + sessionId); }
    catch (e) { return null; }
  }

  function clearThumbs() {
    try {
      var kill = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && k.indexOf(THUMB_KEY_PREFIX) === 0) kill.push(k);
      }
      for (var j = 0; j < kill.length; j++) localStorage.removeItem(kill[j]);
    } catch (e) {}
  }

  // ===== EXPORT ============================================================

  window.TSSQuizVision = {
    isEnabled: isEnabled,
    preflight: preflight,
    processFile: processFile,
    analyse: analyse,
    maxImages: maxImages,
    buildReconcile: buildReconcile,
    applyFindings: applyFindings,
    confirmableReads: confirmableReads,
    findConflicts: findConflicts,
    applyChoices: applyChoices,
    saveThumb: saveThumb,
    loadThumb: loadThumb,
    clearThumbs: clearThumbs,
    endpoint: VISION_ENDPOINT
  };

  log('ready', isEnabled() ? 'enabled' : 'DISABLED (no endpoint)');
})();
